class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.handleDrawerClick = this.handleDrawerClick.bind(this);
    this.handleDrawerChange = this.handleDrawerChange.bind(this);
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('click', this.handleDrawerClick, true);
    this.addEventListener('change', this.handleDrawerChange, true);
    this.setHeaderCartIconAccessibility();
  }

  handleDrawerClick(event) {
    const overlay = event.target.closest('#CartDrawer-Overlay');
    if (overlay && this.contains(overlay)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.close();
      return;
    }

    const removeButton = event.target.closest('[data-cart-remove]');
    if (removeButton && this.contains(removeButton)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const removeElement = removeButton.closest('cart-remove-button');
      this.updateCartLine(Number(removeElement?.dataset.index), 0, removeButton);
      return;
    }

    const quantityButton = event.target.closest('.quantity__button');
    if (!quantityButton || !this.contains(quantityButton)) return;

    const quantityInput = quantityButton.closest('quantity-input')?.querySelector('[name="updates[]"]');
    if (!quantityInput) return;
    event.preventDefault();
    event.stopImmediatePropagation();

    const previousValue = Number(quantityInput.value);
    if (quantityButton.name === 'plus') quantityInput.stepUp();
    else quantityInput.stepDown();
    const nextValue = Math.max(0, Number(quantityInput.value));
    if (nextValue === previousValue) return;
    this.updateCartLine(Number(quantityInput.dataset.index), nextValue, quantityButton);
  }

  handleDrawerChange(event) {
    const quantityInput = event.target.closest('[name="updates[]"]');
    if (!quantityInput || !this.contains(quantityInput)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const quantity = Math.max(0, Number(quantityInput.value));
    this.updateCartLine(Number(quantityInput.dataset.index), quantity, quantityInput);
  }

  async updateCartLine(line, quantity, trigger) {
    if (!Number.isInteger(line) || line < 1 || !Number.isFinite(quantity) || this.classList.contains('is-updating')) return;

    const scrollContainer = this.querySelector('cart-drawer-items');
    const previousScrollTop = scrollContainer?.scrollTop || 0;
    this.classList.add('is-updating');
    this.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(window.routes?.cart_change_url || '/cart/change.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          line,
          quantity,
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname
        })
      });
      const state = await response.json();
      if (!response.ok || state.status) throw new Error(state.description || window.cartStrings?.error || 'Unable to update your cart.');

      this.renderUpdatedCart(state);
      this.classList.toggle('is-empty', state.item_count === 0);
      this.classList.add('active', 'animate');

      window.requestAnimationFrame(() => {
        const nextScrollContainer = this.querySelector('cart-drawer-items');
        if (nextScrollContainer) nextScrollContainer.scrollTop = previousScrollTop;

        if (quantity > 0) {
          const nextInput = this.querySelector(`#Drawer-quantity-${line}`);
          nextInput?.focus({ preventScroll: true });
        } else {
          const nextFocus = this.querySelector('[data-cart-remove]') || this.querySelector('.drawer__close');
          nextFocus?.focus({ preventScroll: true });
        }
      });

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: state } }));
    } catch (error) {
      const errorElement = this.querySelector('#CartDrawer-CartErrors');
      if (errorElement) errorElement.textContent = error.message;
      trigger?.focus({ preventScroll: true });
    } finally {
      this.classList.remove('is-updating');
      this.removeAttribute('aria-busy');
    }
  }

  renderUpdatedCart(state) {
    const drawerHtml = state.sections?.['cart-drawer'];
    if (drawerHtml) {
      const nextDrawer = new DOMParser().parseFromString(drawerHtml, 'text/html').querySelector('#CartDrawer');
      const currentDrawer = this.querySelector('#CartDrawer');
      if (nextDrawer && currentDrawer) currentDrawer.innerHTML = nextDrawer.innerHTML;
    }

    const bubbleHtml = state.sections?.['cart-icon-bubble'];
    if (bubbleHtml) {
      const nextBubble = new DOMParser().parseFromString(bubbleHtml, 'text/html').querySelector('#cart-icon-bubble');
      const currentBubble = document.querySelector('#cart-icon-bubble');
      if (nextBubble && currentBubble) currentBubble.innerHTML = nextBubble.innerHTML;
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    drawerInner?.classList.remove('is-empty');
    if (!parsedState?.sections) {
      this.open();
      return;
    }
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      const sectionHtml = parsedState.sections[section.id];
      if (!sectionElement || !sectionHtml) return;
      const innerHtml = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (innerHtml !== null) sectionElement.innerHTML = innerHtml;
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector)?.innerHTML ?? null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
